import { useTranslations } from 'next-intl';
import type { UseFormRegister } from 'react-hook-form';
import type { EvCalculationResponse } from '@//app/api/ev-calculation/[id]';
import { DataList, DataListItem } from '@//components/DataList';
import { Disclaimer } from '@//components/Disclaimer';
import type { SelectOption } from '@//components/Select';
import { SummaryBox } from '@//components/SummaryBox';
import { VStack } from '@//components/VStack';
import { RangeSliderSelectfield } from '@//containers/RangeSliderSelectfield';
import { WidgetDetails } from '../../WidgetDetails';
import type { EvCalculationForm } from '../utils';

interface TemplatePriceProps {
  minDistance: number;
  maxDistance: number;
  distance: number;
  distanceOptions: SelectOption[];
  defaultDistanceValue?: number;
  data?: EvCalculationResponse;
  costPerMonth?: number;
  costPerMonthLabel: string;
  selectedDistance?: string;
  loading?: boolean;
  onChangeDistanceSlider: (value: [number]) => void;
  onChangeDistanceSelect: (value: [number]) => void;
  onBlurDistance: () => void;
  register: UseFormRegister<EvCalculationForm>;
}

export const TemplatePrice: React.FC<TemplatePriceProps> = ({
  minDistance,
  maxDistance,
  distance,
  distanceOptions,
  defaultDistanceValue,
  data,
  costPerMonth,
  costPerMonthLabel,
  selectedDistance,
  loading,
  onChangeDistanceSlider,
  onChangeDistanceSelect,
  onBlurDistance,
  register,
}) => {
  const t = useTranslations('pages.item.chargingCostsSection');
  return (
    <VStack gap={24}>
      <RangeSliderSelectfield
        variant="compact"
        id="ev-charging-template-price-yearly-mileage"
        label={t('yearlyMileage')}
        rangeSlider={{
          id: 'ev-charging-template-price-yearly-mileage-range-slider',
          label: t('yearlyMileage'),
          min: minDistance,
          max: maxDistance,
          step: 500,
          defaultValue: defaultDistanceValue ? [defaultDistanceValue] : undefined,
          value: [Number.isNaN(distance) ? minDistance : distance],
          onChange: onChangeDistanceSlider,
        }}
        selectfield={{
          id: 'ev-charging-template-price-yearly-mileage-select',
          label: t('yearlyMileage'),
          options: distanceOptions,
          defaultValue: defaultDistanceValue,
          ...register('distance', {
            valueAsNumber: true,
            onBlur: onBlurDistance,
            onChange: (e) => {
              const value = Number.parseInt(e.target.value, 10);
              if (!Number.isNaN(value)) {
                onChangeDistanceSelect([value]);
              }
            },
          }),
        }}
      />
      <SummaryBox label={t('chargingCost')} value={loading ? t('loading') : costPerMonthLabel} />
      <WidgetDetails
        id="ev-charging-template-price-details"
        distance={selectedDistance}
        chargingCost={costPerMonth}
      >
        <Disclaimer>{t('disclaimers.templatePrice')}</Disclaimer>
        <DataList>
          <DataListItem
            label={t('details.vehicleBatteryCapacity')}
            value={
              data
                ? `${data.vehicle.batteryCapacity.value} ${data.vehicle.batteryCapacity.unit}`
                : t('details.noValue')
            }
          />
          <DataListItem
            label={t('details.vehicleRange')}
            value={
              data
                ? `${data.vehicle.electricalRangeWLTP.value} ${data.vehicle.electricalRangeWLTP.unit}`
                : t('details.noValue')
            }
          />
          <DataListItem
            label={t('details.specifiedYearlyMileage')}
            value={selectedDistance ? selectedDistance : t('details.noValue')}
          />
          <DataListItem
            label={t('details.electricityPriceTemplate')}
            value={
              data?.description.costPerKwh.value &&
              !Number.isNaN(data?.description.costPerKwh.value)
                ? t('details.electricityPriceUnit', {
                    value: data.description.costPerKwh.value * 100,
                  })
                : t('details.noValue')
            }
          />
          <DataListItem label={t('details.gridFee')} value={t('details.gridFeeNotIncluded')} />
        </DataList>
      </WidgetDetails>
    </VStack>
  );
};
